import React from "react";
import { Button, Alert } from "reactstrap";
import Dropzone from "react-dropzone";
import { connect } from "react-redux";
import ReactCrop from "react-image-crop";
import { Player, BigPlayButton } from "video-react";
import ModalFrame from "../../../toolkit/modalFrame";
import withStyles from "isomorphic-style-loader/withStyles";
import cropStyle from "react-image-crop/dist/ReactCrop.css";
import videoStyle from "video-react/dist/video-react.css";
import VideoRecorder from "react-video-recorder";
import s from "./styles.scss";
import cropUi from "../shared/imageCrop.scss";
import {
  CROP_PRESETS,
  CropPresetId,
  cropForPreset,
  presetIconClass,
  scaleCropDimensions,
} from "../../utils/imageCropPresets";
import get from "lodash.get";
import {
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  withModals,
} from "../../composition";
import { firebaseConnect } from "react-redux-firebase";
import ICharityConfig from "../../../types/ICharityConfig";
import {
  isBlobArray,
  preferredVideoRecorderMimeType,
  buildRecordedVideoDownloadFilename,
} from "../../../utils/helpers";
import { getVideoUrl } from "../../../utils/fetch";
import Fragment from "../../../toolkit/fragment";
import { Col, Row } from "reactstrap";
import { captureFrames } from "../../../utils/videoUtils";
import { CircularProgress } from "@mui/material";
import { ScaleLoader } from "react-spinners";
import { formatDurationMinutesSeconds } from "../../../utils/onboarding/dashboardSequencing";

const VIDEO_FRAME_LOAD_TIMEOUT_MS = 25000;

function isDesktopSafari(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent;
  return (
    /safari/i.test(ua) && !/(chrome|chromium|crios|edg|opr|fxios)/i.test(ua)
  );
}

/**
 * Safari/WebKit: first MediaRecorder chunks are often emitted after the
 * library default 500ms sanity window; widen only on Safari browsers.
 */
function safariVideoRecorderOptions(): {
  dataAvailableTimeout?: number;
  replayVideoAutoplayAndLoopOff?: boolean;
  showReplayControls?: boolean;
} {
  if (!isDesktopSafari()) {
    return {};
  }
  return {
    dataAvailableTimeout: 6000,
    replayVideoAutoplayAndLoopOff: true,
    showReplayControls: true,
  };
}

interface Props extends ServiceCallerProps, ModalProps {
  isOpen: boolean;
  toggle: (blobs?: Array<Blob | string>) => void;
  mediaBlobs: Array<Blob | string>;
  charityConfig: ICharityConfig;
  onlyImage: boolean;

  thumbnailBlob?: Blob;
  hideCustomThumbnail?: boolean;
  existingPreviewUrl?: string;
  existingPreviewIsCustom?: boolean;
  showCustomThumbnailModal?: boolean;
  mediaModalPostTitle?: string;
}

interface State {
  dropped: boolean;
  recording: boolean;
  currentImageBlobUrl?: string;
  currentImageBlob?: Blob;
  cropImage: HTMLImageElement;
  crop?: any;
  pixelCrop?: any;
  videoBlobUrl?: string;
  videoBlob?: Blob;
  isVideo: boolean;
  gettingDownloadLink: boolean;
  thumbnailBlob?: Blob;
  videoFrames?: Blob[];
  showThumbnailStrip: boolean;
  thumbnailStripLoading: boolean;
  cropPreset: CropPresetId;
  selectedFrameIndex: number | null;
  selectedUploadIndex: number | null;
  thumbnailSource: "upload" | "frame" | "existing" | null;
  uploadedThumbnails: Blob[];
  persistedCustomThumbnailBlob?: Blob;
  persistedCustomThumbnailPreviewUrl?: string;
}

class MediaModal extends React.Component<Props, State> {
  private objectUrls = new Map<Blob, string>();

  /** One object URL per Blob for this modal session; all are revoked when the modal unmounts. */
  objectUrl = (blob: Blob): string => {
    let url = this.objectUrls.get(blob);
    if (!url) {
      url = URL.createObjectURL(blob);
      this.objectUrls.set(blob, url);
    }
    return url;
  };

  static defaultProps = {
    onlyImage: false,
  };

  pendingThumbnailSource: "upload" | null = null;

  state = {
    recording: false,
    cropImage: null,
    isVideo: false,
    dropped: false,
    gettingDownloadLink: false,
    currentImageBlobUrl: null,
    currentImageBlob: null,
    videoBlobUrl: null,
    videoBlob: null,
    crop: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    pixelCrop: {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    },
    videoFrames: [],
    showThumbnailStrip: false,
    thumbnailStripLoading: false,
    cropPreset: "original",
    selectedFrameIndex: null,
    selectedUploadIndex: null,
    thumbnailSource: null,
    uploadedThumbnails: [],
  };

  openUploadThumbnail = () => {
    this.pendingThumbnailSource = "upload";
    this.props.toggleCustomThumbnailModal();
  };

  openReplaceThumbnailForExistingVideo = () => {
    const previewUrl = this.getExistingPreviewUrl();
    const needsLoad =
      this.state.videoFrames.length === 0 || Boolean(previewUrl);
    this.setState(
      {
        showThumbnailStrip: true,
        thumbnailStripLoading: needsLoad,
        persistedCustomThumbnailPreviewUrl: previewUrl || undefined,
      },
      () => {
        if (!needsLoad) {
          this.applyDefaultThumbnailStripSelection(previewUrl);
          return;
        }
        void this.loadThumbnailStripAssets()
          .then(() => this.applyDefaultThumbnailStripSelection(previewUrl))
          .finally(() => this.setState({ thumbnailStripLoading: false }));
      }
    );
  };

  applyDefaultThumbnailStripSelection = (previewUrl: string | null) => {
    if (previewUrl) {
      this.selectExistingPreview(previewUrl);
    } else if (this.state.videoFrames.length > 0) {
      this.selectVideoFrame(this.state.videoFrames[0], 0);
    }
  };

  loadThumbnailStripAssets = async (): Promise<void> => {
    const previewUrl = this.getExistingPreviewUrl();
    const tasks: Promise<void>[] = [];
    if (this.state.videoFrames.length === 0 && this.state.videoBlobUrl) {
      tasks.push(this.loadVideoFrames(this.state.videoBlobUrl));
    }
    if (previewUrl) {
      tasks.push(this.preloadPreviewImage(previewUrl));
    }
    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
  };

  preloadPreviewImage = (url: string): Promise<void> => {
    return new Promise(resolve => {
      const img = new Image();
      if (url.startsWith("http")) {
        img.crossOrigin = "anonymous";
      }
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
  };

  getExistingPreviewUrl = (): string | null => {
    if (this.props.existingPreviewUrl) {
      return this.props.existingPreviewUrl;
    }
    const first = this.props.mediaBlobs?.[0];
    return typeof first === "string" ? first : null;
  };

  loadPreviewBlob = async (url: string): Promise<Blob | null> => {
    try {
      const response = await fetch(url);
      return await response.blob();
    } catch (error) {
      console.error("Failed to load preview image blob", error);
      return null;
    }
  };

  selectExistingPreview = (url: string) => {
    this.setState({
      thumbnailSource: "existing",
      selectedFrameIndex: null,
      selectedUploadIndex: null,
      currentImageBlobUrl: url,
      persistedCustomThumbnailPreviewUrl: url,
    });
    void this.loadPreviewBlob(url).then(blob => {
      if (!blob) {
        return;
      }
      this.setState({
        persistedCustomThumbnailBlob: blob,
        currentImageBlob: blob,
      });
      this.props.resetCustomThumbnailModal(blob, false);
    });
  };

  backFromThumbnailStrip = () => {
    const previewUrl =
      typeof this.props.mediaBlobs?.[0] === "string"
        ? this.props.mediaBlobs[0]
        : this.state.currentImageBlobUrl;
    this.setState({
      showThumbnailStrip: false,
      thumbnailStripLoading: false,
      currentImageBlob: null,
      currentImageBlobUrl: previewUrl,
      selectedFrameIndex: null,
      selectedUploadIndex: null,
      thumbnailSource: null,
    });
    this.props.resetCustomThumbnailModal(null, false);
  };

  selectUploadedThumbnail = (blob: Blob, index: number) => {
    this.props.resetCustomThumbnailModal(blob, false);
    this.setState({
      selectedUploadIndex: index,
      selectedFrameIndex: null,
      thumbnailSource: "upload",
      currentImageBlob: blob,
      currentImageBlobUrl: this.objectUrl(blob),
    });
  };

  selectVideoFrame = (blob: Blob, index: number) => {
    this.props.resetCustomThumbnailModal(blob, false);
    this.setState({
      selectedFrameIndex: index,
      selectedUploadIndex: null,
      thumbnailSource: "frame",
      currentImageBlob: blob,
      currentImageBlobUrl: this.objectUrl(blob),
    });
  };

  isCustomThumbnailUploadSelected = (): boolean => {
    if (this.state.thumbnailSource === "upload") {
      return true;
    }
    if (this.state.thumbnailSource === "existing") {
      return this.props.existingPreviewIsCustom === true;
    }
    return false;
  };

  resolveFinishThumbnailBlob = (): Blob | null => {
    if (this.state.thumbnailSource === "existing") {
      return this.state.persistedCustomThumbnailBlob || null;
    }
    if (this.isCustomThumbnailUploadSelected() && this.props.thumbnailBlob) {
      return this.props.thumbnailBlob;
    }
    if (this.state.thumbnailSource === "frame" && this.state.selectedFrameIndex != null) {
      return this.state.videoFrames[this.state.selectedFrameIndex] || null;
    }
    if (
      this.state.thumbnailSource === "upload" &&
      this.state.selectedUploadIndex != null
    ) {
      return (
        this.state.uploadedThumbnails[this.state.selectedUploadIndex] || null
      );
    }
    return this.props.thumbnailBlob || this.state.currentImageBlob || null;
  };

  onDrop = (accepted: Array<File>) => {
    accepted.forEach(file => {
      const blobUrl = this.objectUrl(file);
      const isVideo = file.type.startsWith("video");
      this.setState(
        {
          dropped: true,
          recording: false,
          isVideo: isVideo,
          currentImageBlobUrl: isVideo ? null : blobUrl,
          currentImageBlob: isVideo ? null : file,
          videoBlobUrl: isVideo ? blobUrl : null,
          videoBlob: isVideo ? file : null,
          cropPreset: isVideo ? this.state.cropPreset : "original",
          showThumbnailStrip:
            isVideo && !this.props.hideCustomThumbnail,
          thumbnailStripLoading:
            isVideo && !this.props.hideCustomThumbnail,
        },
        () => {
          if (isVideo) {
            void this.loadVideoFrames(this.state.videoBlobUrl).finally(() => {
              this.setState({ thumbnailStripLoading: false });
            });
          }
        }
      );
    });
  };

  onCropImageLoaded = (image: HTMLImageElement) => {
    this.setState({
      cropImage: image,
      crop: cropForPreset(this.state.cropPreset, image),
    });
  };

  onCropPresetChange = (presetId: CropPresetId) => {
    const image = this.state.cropImage;
    if (!image) {
      this.setState({ cropPreset: presetId });
      return;
    }
    this.setState({
      cropPreset: presetId,
      crop: cropForPreset(presetId, image),
    });
  };

  onCropChange = (crop, pixelCrop) => {
    this.setState({ crop: crop, pixelCrop: pixelCrop });
  };

  returnToDropzone = () => {
    this.setState({
      currentImageBlobUrl: null,
      currentImageBlob: null,
      videoBlobUrl: null,
      videoBlob: null,
      isVideo: false,
      dropped: false,
      recording: false,
      thumbnailBlob: null,
      showThumbnailStrip: false,
      thumbnailStripLoading: false,
      videoFrames: [],
      cropPreset: "original",
      selectedFrameIndex: null,
      selectedUploadIndex: null,
      thumbnailSource: null,
      uploadedThumbnails: [],
      persistedCustomThumbnailBlob: undefined,
      persistedCustomThumbnailPreviewUrl: undefined,
    });
    this.props.resetCustomThumbnailModal(null, false);
  };

  downloadFile = () => {
    if (this.state.isVideo) {
      const videoId = this.getVideoId();
      if (videoId) {
        this.setState(
          {
            gettingDownloadLink: true,
          },
          () => {
            getVideoUrl(
              this.props.config.adminServiceUrl,
              this.props.currentCharity.id,
              videoId,
              this.props.serviceAccessToken
            )
              .then(url => {
                window.open(url, "_blank");
                this.setState({
                  gettingDownloadLink: false,
                });
              })
              .catch(error => {
                console.error(
                  "something went wrong while getting the download url: ",
                  error
                );
                this.setState({
                  gettingDownloadLink: false,
                });
              });
          }
        );
      } else if (this.state.videoBlobUrl) {
        window.open(this.state.videoBlobUrl, "_blank");
      } else {
        console.error("there is a problem with the video URL.");
      }
    } else {
      window.open(this.state.currentImageBlobUrl, "_blank");
    }
  };

  getVideoId = (): string => {
    if (!this.state.videoBlobUrl) {
      return null;
    }
    const index = this.state.videoBlobUrl.toString().indexOf("vimeo://");
    if (index !== -1) {
      return this.state.videoBlobUrl.toString().substring(index + 8);
    }
    return null;
  };

  updateVideoFrames = imagesBlob => {
    this.setState({ videoFrames: imagesBlob });
  };

  generateVideoFrames = (): Promise<void> => {
    if (!this.state.isVideo || !this.state.videoBlobUrl) {
      return Promise.resolve();
    }
    return this.loadVideoFrames(this.state.videoBlobUrl);
  };

  downloadRecordedVideo = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const filename = buildRecordedVideoDownloadFilename(
      this.props.currentCharity?.name || "Charity",
      this.props.mediaModalPostTitle,
      blob
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  loadVideoFrames = (videoUrl: string): Promise<void> => {
    return new Promise(resolve => {
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        resolve();
      };
      const timeoutId = setTimeout(() => {
        if (settled) {
          return;
        }
        console.error("Timed out loading video frames");
        alert(
          "Could not prepare video thumbnails. Please try again or choose another video."
        );
        this.returnToDropzone();
        finish();
      }, VIDEO_FRAME_LOAD_TIMEOUT_MS);
      const finishAndClear = () => {
        clearTimeout(timeoutId);
        finish();
      };

      if (
        !this.state.videoBlob &&
        !videoUrl.startsWith("http") &&
        !videoUrl.startsWith("blob:") &&
        !videoUrl.startsWith("vimeo://")
      ) {
        finishAndClear();
        return;
      }

      void (async () => {
        let captureUrl = videoUrl;
        if (videoUrl.startsWith("vimeo://")) {
          const videoId = this.getVideoId();
          if (!videoId) {
            finishAndClear();
            return;
          }
          try {
            this.setState({ gettingDownloadLink: true });
            captureUrl = await getVideoUrl(
              this.props.config.adminServiceUrl,
              this.props.currentCharity.id,
              videoId,
              this.props.serviceAccessToken
            );
          } catch (error) {
            console.error(
              "Failed to resolve Vimeo URL for thumbnail frames",
              error
            );
            alert(
              "Could not load video frames. You can still upload a custom thumbnail."
            );
            finishAndClear();
            return;
          } finally {
            this.setState({ gettingDownloadLink: false });
          }
        }

        const video: HTMLVideoElement = document.createElement("video");
        if (captureUrl.startsWith("http")) {
          video.crossOrigin = "anonymous";
        }

        const onLoadedMetadata = async () => {
          video.removeEventListener("loadedmetadata", onLoadedMetadata);
          video.pause();

          const duration = video.duration;
          const max = get(this.props.charityConfig, "maxVideoLength", 90);

          if (Math.floor(duration) > max) {
            alert(
              `Your video is too long (${formatDurationMinutesSeconds(
                duration
              )}). It must be shorter than ${formatDurationMinutesSeconds(max)}.`
            );
            this.returnToDropzone();
            finishAndClear();
            return;
          }

          if (video.videoWidth == 0 && video.videoHeight == 0) {
            alert(
              "The current format of the video is not supported by the browser. Please try using safari, our mobile apps or choosing a different file."
            );
            this.returnToDropzone();
            finishAndClear();
            return;
          }

          try {
            const frames = await captureFrames(video);

            if (frames.length === 0) {
              alert(
                "Error reading the video, please try again or choose another video."
              );
              this.returnToDropzone();
              finishAndClear();
              return;
            }

            this.setState(
              prevState => {
                if (prevState.thumbnailSource === "existing") {
                  return { videoFrames: frames };
                }
                return {
                  videoFrames: frames,
                  currentImageBlobUrl:
                    prevState.currentImageBlobUrl ||
                    this.objectUrl(frames[0]),
                  currentImageBlob: prevState.currentImageBlob || frames[0],
                  thumbnailSource: prevState.thumbnailSource || "frame",
                  selectedFrameIndex:
                    prevState.selectedFrameIndex == null
                      ? 0
                      : prevState.selectedFrameIndex,
                };
              },
              finishAndClear
            );
          } catch (error) {
            console.error("Error during frame capture:", error);
            alert(
              "Error reading the video, please try again or choose another video."
            );
            this.returnToDropzone();
            finishAndClear();
          }
        };

        video.onerror = e => {
          alert(
            "Error reading the video, please try again or choose another video."
          );
          console.error("Error reading video", e);
          this.returnToDropzone();
          finishAndClear();
        };

        video.addEventListener("loadedmetadata", onLoadedMetadata);
        video.src = captureUrl;
        video.load();
      })();
    });
  };

  finish = async () => {
    if (this.state.isVideo) {
      const urlOnly = !this.state.videoBlob && Boolean(this.state.videoBlobUrl);
      const customThumbnailUpload = this.isCustomThumbnailUploadSelected();
      let finishThumbnail = this.resolveFinishThumbnailBlob();
      if (
        !finishThumbnail &&
        this.state.thumbnailSource === "existing" &&
        this.getExistingPreviewUrl()
      ) {
        finishThumbnail = await this.loadPreviewBlob(this.getExistingPreviewUrl());
      }
      if (
        !finishThumbnail &&
        this.state.videoFrames?.length > 0 &&
        this.state.thumbnailSource !== "existing"
      ) {
        const index = this.state.selectedFrameIndex ?? 0;
        finishThumbnail = this.state.videoFrames[index] || null;
      }
      if (urlOnly && !this.props.hideCustomThumbnail && finishThumbnail) {
        this.props.toggleMediaModal(
          [finishThumbnail, this.state.videoBlobUrl],
          this.props.onlyImage,
          this.props.hideCustomThumbnail,
          customThumbnailUpload
        );
        return;
      }
      if (finishThumbnail || this.state.currentImageBlob) {
        this.props.toggleMediaModal(
          [finishThumbnail || this.state.currentImageBlob, this.state.videoBlob],
          this.props.onlyImage,
          this.props.hideCustomThumbnail,
          customThumbnailUpload
        );
      }
    } else {
      const resultImage: Promise<HTMLImageElement> = new Promise(resolve =>
        resolve(this.state.cropImage)
      );

      const maxWidth = get(this.props.charityConfig, "maxImageSize", 1000);
      const finalCanvas = document.createElement("canvas");
      const finalContext = finalCanvas.getContext("2d");
      resultImage.then(image => {
        const sourceWidth = (this.state.crop.width / 100) * image.naturalWidth;
        const sourceHeight =
          (this.state.crop.height / 100) * image.naturalHeight;

        const scaled = scaleCropDimensions(sourceWidth, sourceHeight, maxWidth);
        finalCanvas.width = scaled.width;
        finalCanvas.height = scaled.height;

        finalContext.drawImage(
          image,
          (this.state.crop.x / 100) * image.naturalWidth,
          (this.state.crop.y / 100) * image.naturalHeight,
          sourceWidth,
          sourceHeight,
          0,
          0,
          finalCanvas.width,
          finalCanvas.height
        );

        finalCanvas.toBlob(
          blob => {
            this.props.toggleMediaModal(
              [blob],
              this.props.onlyImage,
              this.props.hideCustomThumbnail,
              false
            );
          },
          "image/jpeg",
          10
        );
      });
    }
  };

  applyMediaBlobs = (mediaBlobs: Array<Blob | string> | undefined) => {
    if (mediaBlobs && mediaBlobs.length > 0) {
      if (isBlobArray(mediaBlobs)) {
        const isVideo = mediaBlobs.length > 1;
        const showStrip = isVideo && !this.props.hideCustomThumbnail;
        this.setState(
          {
            currentImageBlob: mediaBlobs[0],
            currentImageBlobUrl: this.objectUrl(mediaBlobs[0]),
            videoBlob: mediaBlobs[1] || null,
            videoBlobUrl: mediaBlobs[1]
              ? this.objectUrl(mediaBlobs[1])
              : null,
            isVideo,
            dropped: true,
            showThumbnailStrip: showStrip,
            thumbnailStripLoading: showStrip,
          },
          () => {
            if (isVideo && this.state.videoBlobUrl) {
              void this.loadVideoFrames(this.state.videoBlobUrl).finally(() => {
                this.setState({ thumbnailStripLoading: false });
              });
            }
          }
        );
      } else {
        const first = mediaBlobs[0];
        const second = mediaBlobs[1];
        const videoUrl =
          second == null
            ? null
            : typeof second === "string"
            ? second
            : this.objectUrl(second);
        const isVideo = mediaBlobs.length > 1;
        this.setState({
          currentImageBlob: typeof first === "string" ? null : first,
          currentImageBlobUrl:
            typeof first === "string" ? first : this.objectUrl(first),
          videoBlob: null,
          videoBlobUrl: videoUrl,
          isVideo,
          dropped: true,
        });
      }
    } else {
      this.returnToDropzone();
    }
  };

  componentWillUnmount() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }

  componentDidMount() {
    this.applyMediaBlobs(this.props.mediaBlobs);
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.showCustomThumbnailModal &&
      !this.props.showCustomThumbnailModal
    ) {
      if (
        this.props.thumbnailBlob &&
        this.pendingThumbnailSource === "upload"
      ) {
        const blob = this.props.thumbnailBlob;
        this.setState(prevState => {
          const uploadedThumbnails = [...prevState.uploadedThumbnails, blob];
          return {
            uploadedThumbnails,
            selectedFrameIndex: null,
            selectedUploadIndex: uploadedThumbnails.length - 1,
            thumbnailSource: "upload",
            currentImageBlob: blob,
            currentImageBlobUrl: this.objectUrl(blob),
            showThumbnailStrip: true,
          };
        });
      }
      this.pendingThumbnailSource = null;
    }

    if (
      prevProps.hideCustomThumbnail !== this.props.hideCustomThumbnail &&
      this.props.hideCustomThumbnail
    ) {
      this.setState({ showThumbnailStrip: false });
    }
    if (prevProps.mediaBlobs !== this.props.mediaBlobs) {
      this.applyMediaBlobs(this.props.mediaBlobs);
    }
  }

  getNotSupportedMessage = () => {
    return (
      <div>
        This browser is uncapable of recording video. Please use a compatible
        Google Chrome Version.
      </div>
    );
  };

  getEmptyDiv = () => {
    return <div />;
  };

  getRecorderLoadingView = () => {
    return (
      <div className={s.recorderLoadingView}>
        <div className={s.recorderLoadingSpinner}>
          <CircularProgress size={48} thickness={4} sx={{ color: "#fff" }} />
        </div>
      </div>
    );
  };

  recordVideo = () => {
    this.setState({
      recording: true,
    });
  };

  goBack = () => {
    this.setState({
      recording: false,
    });
  };

  render() {
    const { onlyImage } = this.props;
    const maxSize = get(this.props.charityConfig, "videoSizeWarning", 300);
    const accept = onlyImage
      ? ["image/jpeg", "image/png"]
      : [
          "image/jpeg",
          "image/png",
          "video/mp4",
          "video/webm",
          "video/quicktime",
          "video/3gpp",
        ];
    const dropzoneText = onlyImage
      ? " Click here to select your photo or drop it here."
      : "Click here to select your photo or video, or drop it here.";

    const maxTime = get(this.props.charityConfig, "maxVideoLength", 90) * 1000;
    const urlOnlyVideo =
      this.state.isVideo && this.state.videoBlobUrl && !this.state.videoBlob;
    const showCustomThumbnail = !this.props.hideCustomThumbnail;
    const canShowThumbnailStrip =
      showCustomThumbnail &&
      this.state.isVideo &&
      (Boolean(this.state.videoBlob) || urlOnlyVideo);
    const showThumbnailStrip =
      canShowThumbnailStrip && this.state.showThumbnailStrip;
    const currentPreviewUrl =
      this.state.persistedCustomThumbnailPreviewUrl ||
      this.getExistingPreviewUrl();
    const safariRecorder = safariVideoRecorderOptions();
    const recorderMimeType = preferredVideoRecorderMimeType();
    return (
      <ModalFrame
        isOpen={this.props.isOpen}
        toggle={() => this.props.toggle()}
        size="xl"
        className={s.mediaModal}
        title={"Select Media"}
        titleClassName={s.modalTitleCentered}
      >
        {this.state.recording && !this.state.dropped && (
          <Fragment>
            <Row>
              <div className={s.recordVideoRecorderShell}>
                <VideoRecorder
                  {...safariRecorder}
                  mimeType={recorderMimeType}
                  isOnInitially={true}
                  wrapperClassName={s.videoRecorderWrap}
                  isFlipped={true}
                  timeLimit={maxTime}
                  renderDisconnectedView={this.getEmptyDiv}
                  renderLoadingView={this.getRecorderLoadingView}
                  renderUnsupportedView={this.getNotSupportedMessage}
                  onError={err => console.error(err)}
                  onRecordingComplete={videoBlob => {
                    this.downloadRecordedVideo(videoBlob as Blob);
                    let b: any = videoBlob;
                    b.lastModifiedDate = new Date();
                    b.name = "fileName";
                    return this.onDrop([videoBlob as File]);
                  }}
                />
              </div>
            </Row>
            <Button onClick={this.goBack} className={s.recordButton}>
              Go back
            </Button>
          </Fragment>
        )}
        {!this.state.dropped && !this.state.recording && (
          <Fragment>
            <Dropzone
              id={"modalDrop"}
              className={s.dropzone}
              onDrop={this.onDrop}
              accept={accept}
              multiple={false}
            >
              <div className={s.dropzoneContent}>
                <p>
                  <span>{dropzoneText}</span>
                </p>
                <p>
                  <span>
                    {"Accepts JPEG or PNG images" +
                      (!onlyImage ? " and MP4, WebM, or MOV videos." : ".")}
                  </span>
                </p>
                <p>
                  Upload process may take a few minutes depending of file size
                  and internet connection.
                </p>
              </div>
            </Dropzone>

            <Row>
              <Col lg={{ size: 12 }}>
                <Button onClick={this.recordVideo} className={s.recordButton}>
                  Record Video
                </Button>
              </Col>
            </Row>
          </Fragment>
        )}
        {this.state.dropped && (
          <div>
            {!this.state.isVideo && this.state.currentImageBlobUrl && (
              <div className={cropUi.imagePreviewWrap}>
                {this.state.currentImageBlob && (
                  <div>
                    <div className={cropUi.aspectPresets}>
                      {CROP_PRESETS.map(preset => (
                        <button
                          key={preset.id}
                          type="button"
                          title={preset.label}
                          aria-label={preset.label}
                          className={
                            this.state.cropPreset === preset.id
                              ? cropUi.aspectPresetActive
                              : cropUi.aspectPreset
                          }
                          onClick={() => this.onCropPresetChange(preset.id)}
                        >
                          <span
                            className={presetIconClass(preset.id, cropUi)}
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                    <div className={cropUi.cropPreview}>
                      <ReactCrop
                        id={"mediaCrop"}
                        imageStyle={{
                          imageOrientation: "from-image",
                        }}
                        src={this.state.currentImageBlobUrl}
                        crop={this.state.crop}
                        onChange={this.onCropChange}
                        onImageLoaded={this.onCropImageLoaded}
                      />
                    </div>
                  </div>
                )}
                {!this.state.currentImageBlob && (
                  <div className={cropUi.cropPreview}>
                    <a
                      href={this.state.currentImageBlobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={s.previewLink}
                      aria-label="Open full image in new tab"
                    >
                      <img
                        src={this.state.currentImageBlobUrl}
                        className={cropUi.noCrop}
                        alt=""
                      />
                    </a>
                  </div>
                )}
              </div>
            )}
            {this.state.isVideo &&
              this.state.currentImageBlobUrl &&
              this.state.videoBlobUrl &&
              !this.state.videoBlobUrl.startsWith("vimeo://") && (
                <Row>
                  <div className={s.postRecordVideoWrap}>
                    <Player
                      id={"mediaPlayer"}
                      fluid
                      poster={this.state.currentImageBlobUrl}
                      src={this.state.videoBlobUrl}
                      className={s.whiteBackground}
                    >
                      <BigPlayButton position="center" />
                    </Player>
                  </div>
                </Row>
              )}

            {this.state.isVideo &&
              this.state.currentImageBlobUrl &&
              this.state.videoBlobUrl &&
              this.state.videoBlobUrl.startsWith("vimeo://") &&
              !this.state.videoBlobUrl.includes("?h=") && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <iframe
                    style={{ display: "block" }}
                    src={
                      "https://player.vimeo.com/video/" +
                      this.state.videoBlobUrl.substr(
                        8,
                        this.state.videoBlobUrl.length
                      ) +
                      "?title=0&byline=0&portrait=0&badge=0&muted=0&pip=0"
                    }
                    width="438"
                    height="438"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}
            {this.state.isVideo &&
              this.state.currentImageBlobUrl &&
              this.state.videoBlobUrl &&
              this.state.videoBlobUrl.startsWith("vimeo://") &&
              this.state.videoBlobUrl.includes("?h=") && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <iframe
                    style={{ display: "block" }}
                    src={
                      "https://player.vimeo.com/video/" +
                      this.state.videoBlobUrl.substr(
                        8,
                        this.state.videoBlobUrl.length
                      ) +
                      "&title=0&byline=0&portrait=0&badge=0&muted=0&pip=0"
                    }
                    width="438"
                    height="438"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}
            {this.state.isVideo &&
              this.state.videoBlob &&
              Boolean(this.state.videoBlob.size) &&
              this.state.videoBlob.size > maxSize * 1000000 && (
                <Alert color="primary">
                  {"Uploading media may take a few minutes."}
                </Alert>
              )}

            {this.props.mediaBlobs &&
              this.props.mediaBlobs.length > 0 &&
              !this.state.currentImageBlob &&
              !this.state.showThumbnailStrip && (
                <div className={s.modalFooterBar}>
                  <div
                    className={`${s.modalFooterCancelWrap} ${s.modalFooterCancelWrapDivided}`}
                  >
                    <Button
                      color="link"
                      className={s.modalFooterCancel}
                      onClick={() => this.props.toggle()}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className={s.modalFooterActions}>
                    <Button
                      id={"mediaAnotherFile"}
                      color="secondary"
                      outline
                      className={s.modalFooterSecondary}
                      onClick={this.returnToDropzone}
                    >
                      Replace
                    </Button>
                    {showCustomThumbnail && this.state.isVideo && urlOnlyVideo && (
                      <Button
                        id={"mediaReplaceThumbnail"}
                        color="secondary"
                        outline
                        className={s.modalFooterSecondary}
                        onClick={this.openReplaceThumbnailForExistingVideo}
                      >
                        Change Thumbnail
                      </Button>
                    )}
                    <Button
                      id={"mediaDownloadFile"}
                      color="primary"
                      className={s.modalFooterPrimary}
                      onClick={this.downloadFile}
                      disabled={this.state.gettingDownloadLink}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              )}

            {showThumbnailStrip && (
              <div className={s.customThumbnailSection}>
                {this.state.thumbnailStripLoading ? (
                  <div
                    className={s.thumbnailStripLoading}
                    aria-busy="true"
                    aria-label="Loading thumbnail options"
                  >
                    <ScaleLoader color="#1f65b1" loading />
                  </div>
                ) : (
                <div className={s.thumbnailStrip}>
                  <button
                    type="button"
                    className={`${s.thumbnailTile} ${s.thumbnailUpload}`}
                    onClick={this.openUploadThumbnail}
                    aria-label="Upload thumbnail"
                  >
                    <i
                      className={`fa fa-camera ${s.thumbnailUploadIcon}`}
                      aria-hidden="true"
                    />
                  </button>

                  {this.state.videoFrames &&
                    this.state.videoFrames.map((blob, index) => {
                      const isSelected =
                        this.state.thumbnailSource === "frame" &&
                        this.state.selectedFrameIndex === index;
                      return (
                        <button
                          type="button"
                          key={`frame-${index}`}
                          className={`${s.thumbnailTile} ${
                            isSelected ? s.thumbnailTileSelected : ""
                          }`}
                          onClick={() => this.selectVideoFrame(blob, index)}
                          aria-label={`Use video frame ${index + 1} as thumbnail`}
                          aria-pressed={isSelected}
                        >
                          <img
                            className={s.thumbnailImage}
                            src={this.objectUrl(blob)}
                            alt=""
                          />
                          {isSelected && (
                            <span className={s.thumbnailSelectedBadge}>
                              <i className="fa fa-check" aria-hidden="true" />
                            </span>
                          )}
                        </button>
                      );
                    })}

                  {this.state.uploadedThumbnails.map((blob, index) => {
                    const isSelected =
                      this.state.thumbnailSource === "upload" &&
                      this.state.selectedUploadIndex === index;
                    return (
                      <button
                        type="button"
                        key={`upload-${index}`}
                        className={`${s.thumbnailTile} ${
                          isSelected ? s.thumbnailTileSelected : ""
                        }`}
                        onClick={() => this.selectUploadedThumbnail(blob, index)}
                        aria-label={`Use uploaded thumbnail ${index + 1}`}
                        aria-pressed={isSelected}
                      >
                        <img
                          className={s.thumbnailImage}
                          src={this.objectUrl(blob)}
                          alt=""
                        />
                        {isSelected && (
                          <span className={s.thumbnailSelectedBadge}>
                            <i className="fa fa-check" aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {urlOnlyVideo && currentPreviewUrl && (
                      <button
                        type="button"
                        key="existing-preview-thumbnail"
                        className={`${s.thumbnailTile} ${
                          this.state.thumbnailSource === "existing"
                            ? s.thumbnailTileSelected
                            : ""
                        }`}
                        onClick={() => this.selectExistingPreview(currentPreviewUrl)}
                        aria-label="Use current thumbnail"
                        aria-pressed={this.state.thumbnailSource === "existing"}
                      >
                        <img
                          className={s.thumbnailImage}
                          src={currentPreviewUrl}
                          alt=""
                        />
                        {this.state.thumbnailSource === "existing" && (
                          <span className={s.thumbnailSelectedBadge}>
                            <i className="fa fa-check" aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    )}
                </div>
                )}
              </div>
            )}

            {((urlOnlyVideo &&
              showCustomThumbnail &&
              this.state.showThumbnailStrip) ||
              (this.state.showThumbnailStrip &&
                Boolean(this.state.videoBlob)) ||
              (this.state.currentImageBlob && !this.state.showThumbnailStrip) ||
              (this.state.currentImageBlob &&
                this.state.showThumbnailStrip &&
                !urlOnlyVideo)) && (
              <div className={s.modalFooterBar}>
                <div
                  className={`${s.modalFooterCancelWrap} ${s.modalFooterCancelWrapDivided}`}
                >
                  <Button
                    color="link"
                    className={s.modalFooterCancel}
                    onClick={
                      urlOnlyVideo && this.state.showThumbnailStrip
                        ? this.backFromThumbnailStrip
                        : () => this.props.toggle()
                    }
                  >
                    {urlOnlyVideo && this.state.showThumbnailStrip
                      ? "Back"
                      : "Cancel"}
                  </Button>
                </div>
                <div className={s.modalFooterActions}>
                  {!(urlOnlyVideo && this.state.showThumbnailStrip) && (
                    <Button
                      id={"mediaAnotherFile"}
                      color="secondary"
                      outline
                      className={s.modalFooterSecondary}
                      onClick={this.returnToDropzone}
                    >
                      Replace
                    </Button>
                  )}
                  <Button
                    color="primary"
                    className={s.modalFooterPrimary}
                    disabled={this.state.thumbnailStripLoading}
                    onClick={() => void this.finish()}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalFrame>
    );
  }
}

const mapStateToProps = state => {
  const { firebase, user, modals } = state;
  const config = get(firebase, `data.${user.currentCharity.id}.config`);
  return {
    mediaBlobs: modals.mediaBlobs,
    onlyImage: modals.onlyImage,
    charityConfig: config ? config : null,
    thumbnailBlob: modals.thumbnailBlob,
    hideCustomThumbnail: modals.hideCustomThumbnail === true,
    existingPreviewUrl: modals.existingPreviewUrl,
    existingPreviewIsCustom: modals.existingPreviewIsCustom === true,
    showCustomThumbnailModal: modals.showCustomThumbnailModal,
    mediaModalPostTitle: modals.mediaModalPostTitle,
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((_, store) => {
    return store.getState().user.currentCharity.id !== ""
      ? [`${store.getState().user.currentCharity.id}/config`]
      : [];
  })(
    serviceCaller(
      withModals(
        connect(mapStateToProps)(
          withStyles(s, cropUi, cropStyle, videoStyle)(MediaModal)
        )
      )
    )
  )
);
